import { createContext, type ReactNode, useMemo, useReducer } from "react";
import { CategoryNodeType } from "src/molecules/CategoryTree";
import { TreeType } from "src/types/Tree";
import { ROOT_PARENT_ID } from "src/utils/category-tree-helpers";
import { createReducer, type Dispatch } from "src/utils/create-reducer";

export type CategoryTreeState = {
  tree: TreeType<CategoryNodeType>;
  rootIds: string[];
  subRootIds: string[];
  parentIds: string[];
  isLoading: boolean;
  categorySlug?: string;
  resetVersion: number;
};

const initialState: CategoryTreeState = {
  tree: [],
  rootIds: [],
  subRootIds: [],
  parentIds: [],
  isLoading: true,
  categorySlug: undefined,
  resetVersion: 0,
};

export type CategoryTreeAction =
  | {
      type: "SET_TREE";
      payload: TreeType<CategoryNodeType>;
    }
  | {
      type: "SET_LOADING";
      payload: boolean;
    }
  | {
      type: "SET_INITIAL_STATE";
      payload: {
        tree: TreeType<CategoryNodeType>;
        categorySlug?: string;
        isLoading?: boolean;
        canFilterCategorySlug: boolean;
        genderless?: boolean;
      };
    }
  | {
      type: "TRIGGER_RESET";
      payload: void;
    };

export type UseCategoryTreeContextReturn = {
  treeState: CategoryTreeState;
  treeDispatch: Dispatch<CategoryTreeAction>;
};

export type CategoryTreeContextValue = CategoryTreeState & {
  childrenByParentId: Map<string, TreeType<CategoryNodeType>>;
  subRootIdSet: Set<string>;
};

export const CategoryTreeContext = createContext<
  CategoryTreeContextValue | undefined
>(undefined);

export const CategoryTreeDispatch = createContext<
  Dispatch<CategoryTreeAction> | undefined
>(undefined);

export type CategoryTreeActions = {
  hasRootItems: boolean;
  rootItemIdSet: Set<string>;
  handleScrollToTop: () => void;
  onGenderNodeChange?: (slug: string) => void;
};

export function buildCategoryTreeContextValue(
  state: CategoryTreeState
): CategoryTreeContextValue {
  const childrenByParentId = new Map<string, TreeType<CategoryNodeType>>();
  for (const node of state.tree) {
    const existing = childrenByParentId.get(node.parentId);
    if (existing) {
      existing.push(node);
    } else {
      childrenByParentId.set(node.parentId, [node]);
    }
  }

  return {
    ...state,
    childrenByParentId,
    subRootIdSet: new Set(state.subRootIds),
  };
}

export const CategoryTreeActionsContext = createContext<
  CategoryTreeActions | undefined
>(undefined);

export const treeReducer = createReducer<CategoryTreeState, CategoryTreeAction>(
  {
    SET_TREE: (state, payload) => {
      return { ...state, tree: payload };
    },
    SET_LOADING: (state, payload) => {
      return { ...state, isLoading: payload };
    },
    SET_INITIAL_STATE: (
      state,
      { tree, categorySlug, isLoading, canFilterCategorySlug, genderless }
    ) => {
      const matchesSlug = (node: CategoryNodeType): boolean =>
        Boolean(categorySlug && node.slug === categorySlug);

      const genderIds = tree.flatMap((item) =>
        item.parentId === ROOT_PARENT_ID ? [item.itemId] : []
      );

      const rootIds = tree.flatMap((category) => {
        if (
          (!genderless && canFilterCategorySlug && matchesSlug(category)) ||
          (!genderless &&
            !canFilterCategorySlug &&
            genderIds.includes(category.parentId)) ||
          (genderless && genderIds.includes(category.itemId))
        ) {
          return [category.itemId];
        }

        return [];
      });

      const subRootIds = tree.flatMap((category) => {
        if (
          (genderless &&
            !genderIds.includes(category.itemId) &&
            genderIds.includes(category.parentId)) ||
          (!genderless && genderIds.includes(category.parentId))
        ) {
          return [category.itemId];
        }

        return [];
      });

      const parentIds = tree.flatMap((category) => {
        if (rootIds.includes(category.parentId)) {
          return [category.itemId];
        }

        return [];
      });

      return {
        ...state,
        tree,
        parentIds,
        rootIds,
        subRootIds,
        isLoading: false,
        categorySlug,
        genderless,
      };
    },
    TRIGGER_RESET: (state) => {
      return { ...state, resetVersion: state.resetVersion + 1 };
    },
  }
);

export function CategoryTreeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [treeState, treeDispatch] = useReducer(treeReducer, initialState);

  const treeValue = useMemo<CategoryTreeContextValue>(
    () => buildCategoryTreeContextValue(treeState),
    [treeState]
  );

  return (
    <CategoryTreeContext.Provider value={treeValue}>
      <CategoryTreeDispatch.Provider value={treeDispatch}>
        {children}
      </CategoryTreeDispatch.Provider>
    </CategoryTreeContext.Provider>
  );
}
